import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Subject } from "rxjs";
import { DirectLinkService } from "./directlink.service";
import { NotificationsService } from "../shared/services/notifications.service";
import { ViewStateService } from "../shared/services/view-state.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-directlink",
  templateUrl: "./directlink.component.html",
  styleUrls: ["./directlink.component.css"],
})
export class DirectLinkComponent implements OnInit, OnDestroy {
  profileForm: UntypedFormGroup;
  profile: any;

  formLevelErrors: string[] = [];

  x = "";
  token = "";
  email = "";
  submitted = false;
  optOutInfo: boolean;
  directoryStatus="";
  
  validFormCheck = true;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private directLinkService: DirectLinkService,
    private viewState: ViewStateService,
    private router: Router,
    private notification: NotificationsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    const root = this.route.snapshot.root;
    this.token = root.queryParams["guid"];
    this.x = root.queryParams["x"];
    this.getProfileDetails(this.token, this.x);
    this.buildForm();
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();
    this.formValues.directoryOptOut="N"; //only option is to opt in to online verification if going through this process
    this.formValues = updatedFormValues;
    this.validateForm();
    //this.getProfileDetails(this.token, this.x); //token=guid
    this.updateProfile(this.token, this.x, this.directoryStatus);
  }
  cancel(event: Event) {
    event.preventDefault();

    this.profileForm.reset();
    this.router.navigate(["/"]);
  }

  private getProfileDetails(guid: string, email: string) {
    this.directLinkService
      .getSetting(guid, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (details) => {          
          this.profile = details;
          this.email = details.email;
          this.profile.userId = guid;
          //this.mapBooleanToStingValues(this.profileForm.value);
          this.mapStingToBooleanValues(details);
          this.profile.directoryOptOut= this.optOutInfo;
          this.profileForm.patchValue(this.profile);
          this.viewState.stopLoading();
        },
        (err) => {
          this.notification.error(
            "Failed",
            "Record could not be located, please login to update preferences."
          );
          this.viewState.stopLoading();
          this.router.navigate(["/"]);
          console.error(err);
        }
      );
  }

  isOptionChanges(values: any) {
    let changed = false;
    if (
      values.optOutInfo !== this.optOutInfo 
    )
      {changed = true;}
    return changed;
  }

  mapBooleanToStingValues(values: any) {
    if (values.optOutInfo) {this.directoryStatus="O";}
    else {this.directoryStatus = "N";}
  }

   mapStingToBooleanValues(values: any) {
    if (values.directoryOptOut==="O") {
      this.optOutInfo=true;
    } else 
    {
      this.optOutInfo=false;
    }
    this.profileForm.get("directoryOptOut").patchValue(this.optOutInfo);
  }
  
  private validateForm() {
    for (const control in this.profileForm.controls) {
      this.profileForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.profileForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.validFormCheck = false;
      this.viewState.stopLoading();
    } else {
      this.validFormCheck = true;
    }
  }

  private buildForm(): void {
    this.profileForm = this.formBuilder.group({
      email: [
        this.formValues.email,
        [Validators.required, Validators.maxLength(100), Validators.email],
      ],
      directoryOptOut: [this.formValues.directoryOptOut],
      personId: [this.formValues.personId],
    });
  }

  private updateProfile(guid: string, email: string, option: any) {
    if (this.profileForm.valid) {
      this.mapBooleanToStingValues(this.profileForm.value);
      this.directLinkService
        .updateSetting(guid, email, option)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (_resp) => {
            this.viewState.stopLoading();
            this.notification.success(
              "Success",
              "Setting updated."
            );
            this.submitted = true;
          },
          (errors) => {
            this.notification.error(
              "Failed",
              "The setting has not been updated."
            );
            this.mapServerErrorsToForm(errors.error);
            this.viewState.stopLoading();
          }
        );
    } else {
      this.viewState.stopLoading();
    }
  }

  private mapServerErrorsToForm(errorObj: any) {
    const errors = errorObj;
    errors.map((error) => {
      this.formErrors[error.fieldName] = "";
      const control = this.profileForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) {
        this.formLevelErrors.push(error.message);
      }
    });
    this.viewState.stopLoading();
  }

  private formValues = {
    email: "",
    directoryOptOut: "",
    personId: "",
  };

  formErrors = {
    email: "",
  };

  validationMessages = {
    email: {
      required: "Email is required.",
      maxlength: "Email must be max 100 characters.",
      email: "Email must be in the correct format.",
    },
  };

  isOptOut(event) {
    let confirm=false;
    if (
      (event.target.checked !== this.optOutInfo) 
    )
    {if (event.target.checked) {
      if (
        window.confirm(
          "I acknowledge that choosing “Ok” confirms my intent to NOT be INCLUDED in the online credential verification system."
        )
      ) {
        this.directoryStatus = "O";        
        this.updateProfile(this.token, this.x, this.directoryStatus);
        confirm=true;
      }
    } else {
      if (
        window.confirm(
          "I acknowledge that choosing “Ok” confirms my intent to be INCLUDED in the online credential verification system."
        )
      ) {
        this.directoryStatus="N";
        this.profileForm.patchValue({
          directoryOptOut: false,
        });        
        this.updateProfile(this.token, this.x, this.directoryStatus);
        confirm=true;
      }
    }}
    if(!confirm) {
      //reset
      this.profileForm.patchValue({
          directoryOptOut: this.optOutInfo,
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
